import { ChevronRightIcon } from "@visiblethread/react-components";
import { number } from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectSharepoint } from "slices/sharepoint/selectors";
import { setPath } from "slices/sharepoint/sharepointSlice";

import { fetchSharepointData } from "../helpers";

const EXTRAS = 30;
const TEXT_PADDING = 8;

const getTextWidth = (text, font = "11px Inter", weight = "600") => {
  if (process.env.NODE_ENV === "test") {
    return 0;
  }

  const context = document.createElement("canvas").getContext("2d");
  context.font = `${weight} ${font}`;
  return context.measureText(text).width;
};

const getLevels = (path, parentWidth) => {
  let accumulatedWidth = 0;
  let levels = 0;
  for (const route of path) {
    const routeWidth = getTextWidth(route.name) + TEXT_PADDING + EXTRAS;
    if (accumulatedWidth + routeWidth <= parentWidth) {
      accumulatedWidth += routeWidth;
      levels++;
    } else {
      accumulatedWidth += TEXT_PADDING + EXTRAS + getTextWidth("...");
      if (levels === 0) {
        accumulatedWidth += routeWidth + TEXT_PADDING + EXTRAS;
        levels++;
      }
      break;
    }
  }

  const isTextShortened =
    accumulatedWidth > parentWidth && path[0].name.length >= 9;

  return {
    numOfLevels: levels,
    isTextShortened,
  };
};

const FileExplorerNavigation = ({ maxWidth = 312 }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const dispatch = useDispatch();
  const { path } = useSelector(selectSharepoint);

  const { numOfLevels, isTextShortened } = getLevels(
    path.slice().reverse(),
    maxWidth
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !toggleRef.current?.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, []);

  const renderDropdownButton = () =>
    numOfLevels !== path.length && (
      <div className="file_explorer__navigation__path" ref={toggleRef}>
        <button onClick={() => setDropdownOpen((prev) => !prev)}>...</button>
        {dropdownOpen && (
          <div
            className="file_explorer__navigation__dropdown"
            role="menu"
            ref={menuRef}
          >
            {path.slice(0, -numOfLevels).map((route) => (
              <button
                key={route.name}
                onClick={() => {
                  dispatch(setPath(path.slice(0, path.indexOf(route) + 1)));
                  fetchSharepointData(dispatch, route);
                  setDropdownOpen(false);
                }}
              >
                {route.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );

  const renderNavigationPaths = () =>
    path.slice(-numOfLevels).map((route, index) => {
      const isCurrentFolder = index + 1 === numOfLevels;
      const currentFolder =
        isCurrentFolder && isTextShortened
          ? `${route.name.slice(0, 3)}...${route.name.slice(-3)}`
          : route.name;

      // Only show chevron when it's not the first item or we have a '...' dropdown
      const showChevron = index !== 0 || numOfLevels !== path.length;

      return (
        <div className="file_explorer__navigation__path" key={route.name}>
          {showChevron && <ChevronRightIcon />}
          <button
            onClick={() => {
              dispatch(setPath(path.slice(0, path.indexOf(route) + 1)));
              fetchSharepointData(dispatch, route);
            }}
            title={isCurrentFolder && isTextShortened ? route.name : undefined}
          >
            {currentFolder}
          </button>
        </div>
      );
    });

  return (
    <div className="file_explorer__navigation">
      {renderDropdownButton()}
      {renderNavigationPaths()}
    </div>
  );
};

FileExplorerNavigation.propTypes = {
  maxWidth: number,
};

export default FileExplorerNavigation;
